import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Menu from '../Menu/Menu';
import api from '../../utils/axios.secure';

function unwrapRows(res) {
  const payload = res?.data || res;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload)) return payload;
  return [];
}

function valueOf(row, key, fallback = '-') {
  const value = row?.[key];
  return value === null || value === undefined || value === '' ? fallback : value;
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatNumber(value, digits = 2) {
  const n = toNumber(value);
  if (n === null) return '-';
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(digits);
}

function percentileRank(rows, current) {
  const score = toNumber(current?.score);
  const scores = rows.map((row) => toNumber(row.score)).filter((n) => n !== null);
  if (score === null || !scores.length) return null;
  const belowOrEqual = scores.filter((n) => n <= score).length;
  return Math.round((belowOrEqual / scores.length) * 100);
}

function Detailcontentresult() {
  const { paramsIndex } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setMessage('');

    try {
      const { data: res } = await api.post('/manager/content/contentResult', {});
      const nextRows = unwrapRows(res);
      const found = nextRows.find((row) => String(row.idx) === String(paramsIndex));

      setRows(nextRows);
      setSelected(found || null);

      if (!found) {
        setMessage('선택한 콘텐츠 결과를 찾을 수 없습니다.');
      }
    } catch (error) {
      console.error('detail content result error:', error);
      setRows([]);
      setSelected(null);
      setMessage(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          '콘텐츠 상세 결과를 불러오는 중 오류가 발생했습니다.'
      );
    } finally {
      setLoading(false);
    }
  }, [paramsIndex]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const sameContentRows = useMemo(() => {
    if (!selected) return [];
    return rows.filter((row) => row.content_name === selected.content_name);
  }, [rows, selected]);

  const averageScore = useMemo(() => {
    const scores = sameContentRows.map((row) => toNumber(row.score)).filter((n) => n !== null);
    if (!scores.length) return null;
    return scores.reduce((sum, n) => sum + n, 0) / scores.length;
  }, [sameContentRows]);

  const percentile = useMemo(
    () => percentileRank(sameContentRows.length ? sameContentRows : rows, selected),
    [rows, sameContentRows, selected]
  );

  const metricCards = selected
    ? [
        { label: '점수', value: formatNumber(selected.score, 0), hint: '콘텐츠 수행 점수' },
        { label: '정확도', value: `${formatNumber(selected.accuracy)}%`, hint: '정반응 비율' },
        { label: '정반응', value: formatNumber(selected.correctCount, 0), hint: 'correctCount' },
        { label: '오반응', value: formatNumber(selected.wrongCount, 0), hint: 'wrongCount' },
        { label: '누락', value: formatNumber(selected.missCount, 0), hint: 'missCount' },
        { label: '수행 시간', value: formatNumber(selected.playTime, 0), hint: 'playTime' },
        { label: '반응 시간', value: formatNumber(selected.reactionTime), hint: 'reactionTime' },
        { label: '정반응 시간', value: formatNumber(selected.correctTime), hint: 'correctTime' },
      ]
    : [];

  return (
    <Menu>
      <section className="screen detail-result-screen">
        <div className="screen-head">
          <div>
            <p className="eyebrow">Detail</p>
            <h1 className="screen-title">콘텐츠 상세 결과</h1>
            <p className="screen-copy">선택한 수행 기록의 세부 지표를 확인합니다.</p>
          </div>
          <div className="screen-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate('/contentresult')}>
              목록으로
            </button>
            <button type="button" className="btn btn-primary" onClick={loadDetail} disabled={loading}>
              새로고침
            </button>
          </div>
        </div>

        {loading && <div className="notice info">상세 데이터를 불러오는 중입니다.</div>}
        {message && !loading && <div className="notice">{message}</div>}

        {selected && (
          <>
            <div className="detail-summary card">
              <div>
                <span className="detail-label">아동 번호</span>
                <strong>{valueOf(selected, 'user_id')}</strong>
              </div>
              <div>
                <span className="detail-label">콘텐츠</span>
                <strong>{valueOf(selected, 'content_name')}</strong>
              </div>
              <div>
                <span className="detail-label">모드</span>
                <strong>{valueOf(selected, 'gameMode')}</strong>
              </div>
              <div>
                <span className="detail-label">저장 일시</span>
                <strong>{valueOf(selected, 'saveDate')}</strong>
              </div>
            </div>

            <div className="kpi-grid detail-kpi-grid">
              {metricCards.map((item) => (
                <div className="kpi-card" key={item.label}>
                  <span className="kpi-accent blue" />
                  <p className="kpi-label">{item.label}</p>
                  <strong className="kpi-value">{item.value}</strong>
                  <div className="kpi-meta">
                    <span className="kpi-period">{item.hint}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="detail-grid">
              <div className="card">
                <div className="card-header">
                  <div>
                    <h2 className="card-title">동일 콘텐츠 비교</h2>
                    <p className="card-subtitle">같은 콘텐츠 기록 기준으로 계산했습니다.</p>
                  </div>
                </div>
                <div className="detail-stat-list">
                  <div>
                    <span>내 점수</span>
                    <strong>{formatNumber(selected.score, 0)}</strong>
                  </div>
                  <div>
                    <span>평균 점수</span>
                    <strong>{formatNumber(averageScore, 0)}</strong>
                  </div>
                  <div>
                    <span>백분위</span>
                    <strong>{percentile === null ? '-' : `${percentile}%`}</strong>
                  </div>
                  <div>
                    <span>비교 기록 수</span>
                    <strong>{sameContentRows.length.toLocaleString()}</strong>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div>
                    <h2 className="card-title">원본 payload</h2>
                    <p className="card-subtitle">복호화된 payload 전체입니다.</p>
                  </div>
                </div>
                <pre className="payload-preview">
                  {JSON.stringify(selected.payload || selected, null, 2)}
                </pre>
              </div>
            </div>
          </>
        )}
      </section>
    </Menu>
  );
}

export default Detailcontentresult;
